import { useMutation } from '@tanstack/react-query';
import type { CustomersRelationshipsRequestBody } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';

type Relationship = CustomersRelationshipsRequestBody['relationships'][number];

export async function createRelationships(workspaceId: number, relationships: Relationship[]): Promise<void> {
  await hydraUiApi.customers.createRelationships({
    params: { path: { environment_id: String(workspaceId) } },
    body: { relationships },
  });
}

export function useCreateRelationships(options: { mutationConfig?: HookMutationConfig<void, Relationship[]> } = {}) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: (relationships: Relationship[]) => createRelationships(workspaceId, relationships),
  });
}

export async function deleteRelationships(workspaceId: number, relationships: Relationship[]): Promise<void> {
  await hydraUiApi.customers.deleteRelationships({
    params: { path: { environment_id: String(workspaceId) } },
    // The endpoint matches a relationship on its entity pair, but still
    // length-validates any attributes it is given — so sending a row's own
    // attributes can make that row fail its own deletion. Send the pair only.
    body: { relationships: relationships.map((relationship) => ({ ...relationship, attributes: undefined })) },
  });
}

export function useDeleteRelationships(options: { mutationConfig?: HookMutationConfig<void, Relationship[]> } = {}) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: (relationships: Relationship[]) => deleteRelationships(workspaceId, relationships),
  });
}
